import React from 'react';
import { 
  Search, 
  Filter, 
  ChevronDown, 
  FileText, 
  Users, 
  Calendar, 
  CheckCircle2, 
  Clock,
  Link as LinkIcon,
  X,
  RefreshCw,
  Download,
  Shield,
  AlertCircle,
  Copy,
  ExternalLink,
  Mail,
  ClipboardList // 👈 Ícono de auditoría (SVG, no emoji)
} from 'lucide-react';

export default function DocumentList({
  documents = [],
  searchTerm = "",
  statusFilter = "all",
  viewingLinks = {},
  regeneratingLinks = {},
  loading = false,
  onSearchChange,
  onFilterChange,
  onViewLink,
  onHideLink,
  onCopyLink,
  onRegenerateLink,
  onDownloadOriginal,
  onDownloadSigned,
  onVerify,
  onRefresh,
  onViewAuditTrail, // 👈 Prop para abrir el audit trail
  formatDateTime,
  getTimeRemaining
}) {
  const safeDocuments = Array.isArray(documents) ? documents : [];

  const filteredDocuments = safeDocuments.filter((doc) => {
    const matchesSearch =
      (doc.clientName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (doc.originalName || "").toLowerCase().includes(searchTerm.toLowerCase());
    
    const totalSigners = doc.totalSigners || 1;
    const signedSigners = doc.signedSigners || 0;
    const allSigned = signedSigners === totalSigners && totalSigners > 0;
    const effectiveStatus = allSigned ? 'signed' : (doc.status || 'pending');
    
    const matchesStatus = statusFilter === 'all' || effectiveStatus === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusConfig = (status) => {
    switch (status) {
      case 'signed':
        return {
          bg: 'bg-emerald-50',
          border: 'border-emerald-200',
          text: 'text-emerald-700',
          icon: CheckCircle2,
          label: 'Firmado',
          dot: 'bg-emerald-500',
          buttonBg: 'bg-emerald-600',
          buttonHover: 'hover:bg-emerald-700'
        };
      case 'pending':
        return {
          bg: 'bg-amber-50',
          border: 'border-amber-200',
          text: 'text-amber-700',
          icon: Clock,
          label: 'Pendiente',
          dot: 'bg-amber-500',
          buttonBg: 'bg-amber-600',
          buttonHover: 'hover:bg-amber-700'
        };
      case 'expired':
        return {
          bg: 'bg-red-50',
          border: 'border-red-200',
          text: 'text-red-700',
          icon: AlertCircle,
          label: 'Expirado',
          dot: 'bg-red-500',
          buttonBg: 'bg-red-600',
          buttonHover: 'hover:bg-red-700'
        };
      default:
        return {
          bg: 'bg-slate-50',
          border: 'border-slate-200',
          text: 'text-slate-700',
          icon: FileText,
          label: status,
          dot: 'bg-slate-500',
          buttonBg: 'bg-slate-600',
          buttonHover: 'hover:bg-slate-700'
        };
    }
  };

  const getSignerStatusBadge = (status) => {
    switch (status) {
      case 'signed':
        return {
          bg: 'bg-emerald-100',
          text: 'text-emerald-700',
          border: 'border-emerald-200',
          label: 'Firmado',
          icon: CheckCircle2
        };
      case 'viewed':
        return {
          bg: 'bg-blue-100',
          text: 'text-blue-700',
          border: 'border-blue-200',
          label: 'Visto',
          icon: ExternalLink
        };
      case 'pending':
      default:
        return {
          bg: 'bg-amber-100',
          text: 'text-amber-700',
          border: 'border-amber-200',
          label: 'Pendiente',
          icon: Clock
        };
    }
  };

  const decodeFileName = (str) => {
    if (!str) return str;
    try {
      const bytes = Uint8Array.from(str, c => c.charCodeAt(0));
      const decoded = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
      return decoded;
    } catch (e) {
      return str;
    }
  };

  const stats = safeDocuments.reduce((acc, doc) => {
    const allSigned = (doc.signedSigners || 0) === (doc.totalSigners || 1) && (doc.totalSigners || 1) > 0;
    const effectiveStatus = allSigned ? 'signed' : (doc.status || 'pending');
    
    acc.total += 1;
    if (effectiveStatus === 'signed') acc.signed += 1;
    if (effectiveStatus === 'pending') acc.pending += 1;
    return acc;
  }, { total: 0, signed: 0, pending: 0 });

  return (
    <div className="w-full space-y-4">
      {/* Header con búsqueda y filtros */}
      <div className="w-full bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            <input
              type="text"
              placeholder="Buscar documentos o clientes..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-10 pr-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
            />
          </div>
          
          <div className="relative w-full sm:w-48 lg:w-56">
            <Filter className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            <select
              value={statusFilter}
              onChange={(e) => onFilterChange(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-10 pr-8 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all appearance-none cursor-pointer"
            >
              <option value="all">Todos los estados</option>
              <option value="pending">Pendientes</option>
              <option value="signed">Firmados</option>
              <option value="expired">Expirados</option>
            </select>
            <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
          </div>
          
          <button
            onClick={onRefresh}
            disabled={loading}
            className={`
              flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all whitespace-nowrap
              ${loading 
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed' 
                : 'bg-blue-600 text-white hover:bg-blue-700'
              }
            `}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{loading ? 'Actualizando...' : 'Actualizar'}</span>
          </button>
        </div>
        
        <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-100">
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 rounded-md text-xs font-medium text-slate-600">
            <FileText className="w-3.5 h-3.5" />
            <span>Total: {stats.total}</span>
          </div>
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-100 rounded-md text-xs font-medium text-emerald-700">
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span>Firmados: {stats.signed}</span>
          </div>
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-100 rounded-md text-xs font-medium text-amber-700">
            <Clock className="w-3.5 h-3.5" />
            <span>Pendientes: {stats.pending}</span>
          </div>
        </div>
      </div>

      {loading && (
        <div className="w-full flex flex-col items-center justify-center py-16 bg-white rounded-xl border border-slate-200">
          <div className="animate-spin rounded-full h-10 w-10 border-3 border-slate-200 border-t-blue-600 mb-3"></div>
          <p className="text-slate-600 text-sm font-medium">Cargando documentos...</p>
        </div>
      )}

      {!loading && filteredDocuments.length === 0 && (
        <div className="w-full text-center py-16 bg-white rounded-xl border border-slate-200 border-dashed">
          <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-3">
            <Search className="w-8 h-8 text-slate-300" />
          </div>
          <h3 className="text-base font-semibold text-slate-900 mb-1">
            No se encontraron documentos
          </h3>
          <p className="text-slate-500 text-sm max-w-sm mx-auto mb-4">
            Intenta ajustar los filtros o sube un nuevo documento.
          </p>
          <button
            onClick={() => {
              onSearchChange('');
              onFilterChange('all');
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
          >
            Limpiar filtros
          </button>
        </div>
      )}

      {!loading && filteredDocuments.length > 0 && (
        <div className="w-full space-y-3">
          {filteredDocuments.map((doc) => {
            const totalSigners = doc.totalSigners || 1;
            const signedSigners = doc.signedSigners || 0;
            const allSigned = signedSigners === totalSigners && totalSigners > 0;
            
            const effectiveStatus = allSigned ? 'signed' : (doc.status || 'pending');
            const status = getStatusConfig(effectiveStatus);
            
            const hasMultipleSigners = totalSigners > 1;
            const progressText = hasMultipleSigners ? `${signedSigners}/${totalSigners} firmados` : '';
            
            const docViewing = viewingLinks?.[doc.id] || {};
            const signersList = Array.isArray(docViewing.signers) ? docViewing.signers : [];
            const pendingCount = signersList.filter(s => s.status !== 'signed').length;
            const isModalOpen = !!viewingLinks?.[doc.id];
            
            return (
              <React.Fragment key={doc.id}>
                <div className="w-full bg-white border border-slate-200 rounded-xl p-3 sm:p-4 hover:border-blue-300 hover:shadow-md transition-all duration-200">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-3 sm:gap-4">
                    {/* Icono e Info principal */}
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5 text-blue-600" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-1.5">
                          <h3 className="text-sm font-semibold text-slate-900 truncate" title={decodeFileName(doc.originalName)}>
                            {decodeFileName(doc.originalName)}
                          </h3>
                          
                          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                            <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${status.bg} ${status.border} ${status.text}`}>
                              <div className={`w-1.5 h-1.5 rounded-full ${status.dot}`}></div>
                              <span>{status.label}</span>
                            </div>
                            
                            {hasMultipleSigners && (
                              <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${
                                allSigned 
                                  ? 'bg-emerald-50 border-emerald-200 text-emerald-700' 
                                  : 'bg-blue-50 border-blue-200 text-blue-700'
                              }`}>
                                <Users className="w-3 h-3" />
                                <span>{progressText}</span>
                              </div>
                            )}
                            
                            {!allSigned && effectiveStatus === 'pending' && (
                              <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-medium">
                                <Clock className="w-3 h-3" />
                                <span>{getTimeRemaining(doc.expiresAt)}</span>
                              </div>
                            )}

                            {allSigned && doc.signedAt && (
                              <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-50 border border-slate-200 text-slate-600 text-xs font-medium">
                                <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                                <span>{formatDateTime(doc.signedAt)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                          <div className="flex items-center gap-1.5">
                            <Users className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                            <span className="font-medium text-slate-700 truncate">{doc.clientName || 'N/A'}</span>
                          </div>
                          
                          <div className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                            <span>{formatDateTime(doc.createdAt)}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Botones de acción */}
                    <div className="flex flex-nowrap items-center gap-1.5 lg:border-l lg:border-slate-100 lg:pl-4 flex-shrink-0 mt-3 lg:mt-0 overflow-x-auto pb-1 lg:pb-0">
                      {!allSigned && effectiveStatus === 'pending' && (
                        <>
                          <button
                            onClick={() => onViewLink(doc.id)}
                            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors border border-blue-200 flex-shrink-0"
                            title="Ver links"
                          >
                            <LinkIcon className="w-4 h-4" />
                            <span className="hidden sm:inline">
                              {hasMultipleSigners ? 'Ver Links' : 'Link'}
                            </span>
                          </button>
                          <button
                            onClick={() => onRegenerateLink(doc.id)}
                            disabled={regeneratingLinks[doc.id]}
                            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-700 bg-white hover:bg-slate-50 rounded-lg transition-colors border border-slate-200 disabled:opacity-50 flex-shrink-0"
                            title="Renovar links"
                          >
                            {regeneratingLinks[doc.id] ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <RefreshCw className="w-4 h-4" />
                            )}
                            <span className="hidden sm:inline">Renovar</span>
                          </button>
                        </>
                      )}

                      <button
                        onClick={() => onDownloadOriginal(doc.id, doc.originalName)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-700 bg-white hover:bg-slate-50 rounded-lg transition-colors border border-slate-200 flex-shrink-0"
                        title="Descargar original"
                      >
                        <Download className="w-4 h-4" />
                        <span className="hidden sm:inline">Original</span>
                      </button>

                      {allSigned && (
                        <button
                          onClick={() => onDownloadSigned(doc.id, doc.originalName)}
                          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-white rounded-lg transition-colors flex-shrink-0 bg-emerald-600 hover:bg-emerald-700"
                          title="Descargar firmado"
                        >
                          <Download className="w-4 h-4" />
                          <span className="hidden sm:inline">Firmado</span>
                        </button>
                      )}

                      <button
                        onClick={() => onVerify(doc.id)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors border border-indigo-200 flex-shrink-0"
                        title="Verificar documento"
                      >
                        <Shield className="w-4 h-4" />
                       {/*  <span className="hidden sm:inline">Verificar</span> */}
                      </button>

                      {/* 👇 BOTÓN DE AUDITORÍA: SOLO ÍCONO (Sin texto) */}
                      <button
                        onClick={() => onViewAuditTrail(doc.id)}
                        className="inline-flex items-center justify-center p-2 text-slate-600 bg-white hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors border border-slate-200 flex-shrink-0"
                        title="Ver pista de auditoría"
                      >
                        <ClipboardList className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* MODAL FLOTANTE DE LINKS */}
                {isModalOpen && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
                    <div 
                      className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity"
                      onClick={() => onHideLink(doc.id)}
                    />
                    
                    <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in zoom-in duration-200">
                      <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                            <LinkIcon className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h3 className="font-bold text-white text-lg">
                              Links de Firma
                            </h3>
                            <p className="text-blue-100 text-sm">
                              {signersList.length} firmante(s) • {pendingCount} pendiente(s)
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => onHideLink(doc.id)}
                          className="p-2 hover:bg-white/20 rounded-lg transition-colors text-white"
                          title="Cerrar"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>

                      <div className="p-6 max-h-[60vh] overflow-y-auto">
                        {signersList.length > 0 ? (
                          <div className="space-y-4">
                            {signersList.map((signer, idx) => {
                              const signerStatus = getSignerStatusBadge(signer.status);
                              const StatusIcon = signerStatus.icon;
                              const isSigned = signer.status === 'signed';
                              
                              return (
                                <div 
                                  key={signer.id}
                                  className={`rounded-xl border p-4 transition-all ${
                                    isSigned 
                                      ? 'bg-slate-50 border-slate-200' 
                                      : 'bg-white border-blue-200 hover:border-blue-300 hover:shadow-md'
                                  }`}
                                >
                                  <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-3 min-w-0 flex-1">
                                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm ${
                                        isSigned 
                                          ? 'bg-emerald-100 text-emerald-700' 
                                          : 'bg-blue-100 text-blue-700'
                                      }`}>
                                        {idx + 1}
                                      </div>
                                      <div className="min-w-0 flex-1">
                                        <p className="text-base font-semibold text-slate-900 truncate">
                                          {signer.fullName || 'Firmante'}
                                        </p>
                                        {signer.email && (
                                          <p className="text-sm text-slate-500 truncate flex items-center gap-1">
                                            <Mail className="w-3.5 h-3.5" />
                                            {signer.email}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                    
                                    <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-medium flex-shrink-0 ml-3 ${signerStatus.bg} ${signerStatus.text} ${signerStatus.border}`}>
                                      <StatusIcon className="w-4 h-4" />
                                      <span>{signerStatus.label}</span>
                                    </div>
                                  </div>

                                  {!isSigned && signer.signatureLink && (
                                    <div className="flex gap-2 mt-3">
                                      <input
                                        type="text"
                                        value={signer.signatureLink}
                                        readOnly
                                        onClick={(e) => {
                                          e.target.select();
                                          navigator.clipboard.writeText(signer.signatureLink);
                                        }}
                                        className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm font-mono text-slate-700 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all"
                                      />
                                      <button
                                        onClick={() => onCopyLink(signer.signatureLink)}
                                        className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-semibold flex items-center gap-2 flex-shrink-0"
                                        title="Copiar link"
                                      >
                                        <Copy className="w-4 h-4" />
                                        <span>Copiar</span>
                                      </button>
                                    </div>
                                  )}

                                  {isSigned && signer.signedAt && (
                                    <div className="mt-3 flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 rounded-lg px-4 py-2.5 border border-emerald-200">
                                      <CheckCircle2 className="w-4 h-4" />
                                      <span className="font-medium">Firmado el {formatDateTime(signer.signedAt)}</span>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-center py-12 text-slate-500">
                            <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                            <p className="text-base">No hay firmantes registrados</p>
                          </div>
                        )}
                      </div>

                      {hasMultipleSigners && pendingCount > 0 && (
                        <div className="bg-slate-50 px-6 py-4 border-t border-slate-200">
                          <p className="text-sm text-slate-600 flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-600" />
                            <span>
                              Cada firmante tiene su link único e independiente. Al regenerar, se extenderá la validez para todos los pendientes.
                            </span>
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}